<script lang="ts">
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';

  interface InstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  }

  /** Compact removes the outer panel spacing for use within a landing section. */
  let { compact = false }: { compact?: boolean } = $props();
  let ready = $state(false);
  let standalone = $state(false);
  let installed = $state(false);
  let deferred = $state<InstallPromptEvent | null>(null);
  let prompting = $state(false);
  let message = $state('');
  let device = $state<'ios' | 'android' | 'desktop'>('desktop');
  let iosSafari = $state(true);
  let macSafari = $state(false);
  let open = $state(false);
  const id = $props.id();

  onMount(() => {
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    device = ios ? 'ios' : /Android/.test(ua) ? 'android' : 'desktop';
    const safari = /Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPiOS/.test(ua);
    iosSafari = safari;
    macSafari = !ios && /Mac/.test(navigator.platform) && safari;
    const displayMode = window.matchMedia('(display-mode: standalone)');
    const syncDisplayMode = () => {
      standalone = displayMode.matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true;
      if (standalone) deferred = null;
    };
    const capturePrompt = (event: Event) => {
      const candidate = event as InstallPromptEvent;
      if (standalone || installed || typeof candidate.prompt !== 'function') return;
      event.preventDefault();
      deferred = candidate;
      message = '';
    };
    const didInstall = () => {
      installed = true;
      deferred = null;
    };
    syncDisplayMode();
    displayMode.addEventListener('change', syncDisplayMode);
    window.addEventListener('pageshow', syncDisplayMode);
    window.addEventListener('beforeinstallprompt', capturePrompt);
    window.addEventListener('appinstalled', didInstall);
    ready = true;
    return () => {
      displayMode.removeEventListener('change', syncDisplayMode);
      window.removeEventListener('pageshow', syncDisplayMode);
      window.removeEventListener('beforeinstallprompt', capturePrompt);
      window.removeEventListener('appinstalled', didInstall);
      deferred = null;
    };
  });

  async function install(): Promise<void> {
    const event = deferred;
    if (!event || prompting) return;
    // The browser event is one-use. Call prompt directly in the click gesture.
    deferred = null;
    prompting = true;
    message = '';
    try {
      await event.prompt();
      const choice = await event.userChoice;
      message = choice.outcome === 'accepted'
        ? 'Your browser is finishing the install. Look for Jovii among your apps.'
        : 'You can add Jovii later from your browser menu.';
    } catch {
      message = 'The install prompt could not open. Try the browser steps below.';
    } finally {
      prompting = false;
    }
    // A choice is not proof of installation. Only appinstalled hides this guide.
  }
</script>

{#if ready && !standalone && !installed}
  <section class="install-guide" class:compact aria-label="Add Jovii to your device">
    <details bind:open>
      <summary>
        <span class="guide-icon"><Icon name="plus" size={19} /></span>
        <span class="summary-copy">
          <span class="title">Add Jovii to your device</span>
          <span class="subtitle">Open it from your home screen or desktop.</span>
        </span>
        <span class="chevron"><Icon name={open ? 'up' : 'down'} size={16} /></span>
      </summary>
      <div class="guide-body">
        {#if deferred || prompting}
          <button class="install-button" type="button" onclick={install} disabled={prompting}>
            {prompting ? 'Waiting for your browser…' : 'Install Jovii'}
          </button>
        {/if}
        <p class="status" role="status">{message}</p>
        <label class="device-choice" for={id + '-device'}>
          <span>Instructions for</span>
          <select id={id + '-device'} bind:value={device}>
            <option value="ios">iPhone or iPad</option>
            <option value="android">Android</option>
            <option value="desktop">Computer</option>
          </select>
        </label>
        {#if device === 'ios'}
          {#if !iosSafari}<p>Open this page in Safari first.</p>{/if}
          <ol>
            <li>In Safari, tap <strong>Share</strong> <span class="inline-icon"><Icon name="share" size={17} /></span>. Look for the square with an arrow pointing up. It may be inside the browser menu.</li>
            <li>Scroll through the share options and tap <strong>Add to Home Screen</strong>.</li>
            <li>If shown, leave <strong>Open as Web App</strong> on. Tap <strong>Add</strong>, then open Jovii from your home screen.</li>
          </ol>
          <p class="note">On iPad, Share is usually near the top of Safari.</p>
        {:else if device === 'android'}
          <ol>
            <li>Open this page in <strong>Chrome</strong> and tap the three-dot menu.</li>
            <li>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>, then confirm.</li>
            <li>Open Jovii from your home screen or app drawer.</li>
          </ol>
        {:else}
          {#if macSafari}
            <ol>
              <li>In Safari, open the <strong>File</strong> menu and choose <strong>Add to Dock</strong>.</li>
              <li>Choose <strong>Add</strong>, then open Jovii from your Dock.</li>
            </ol>
            <p class="note">Add to Dock needs Safari on macOS Sonoma or newer.</p>
          {:else}
            <ol>
              <li>Open this page in <strong>Chrome</strong> or <strong>Edge</strong>.</li>
              <li>Look for the install icon in the address bar, or open the browser menu and look for <strong>Install Jovii</strong> or <strong>Apps</strong>.</li>
              <li>Confirm the install, then open Jovii from your apps.</li>
            </ol>
            <p class="note">Using Safari on a Mac? Choose File, then Add to Dock (macOS Sonoma or newer).</p>
          {/if}
        {/if}
        <p class="note">If an install option is missing, you can keep using Jovii in this browser. You will still need an internet connection.</p>
      </div>
    </details>
  </section>
{/if}

<style>
  .install-guide {
    margin-top: var(--space-5);
    border: 1px solid var(--line);
    border-radius: var(--radius-lg);
    background: var(--surface-1);
    color: var(--text);
    text-align: left;
  }
  .compact { margin-top: 0; background: transparent; }
  summary {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-height: 64px;
    padding: var(--space-4);
    cursor: pointer;
    list-style: none;
    border-radius: var(--radius-lg);
    touch-action: manipulation;
  }
  summary::-webkit-details-marker { display: none; }
  .guide-icon { display: flex; color: var(--accent); flex: none; }
  .summary-copy { display: grid; gap: 3px; flex: 1; min-width: 0; }
  .title { font-size: var(--text-sm); font-weight: var(--weight-semi); line-height: 1.4; }
  .subtitle { color: var(--text-dim); font-size: var(--text-xs); line-height: 1.5; }
  .chevron { display: flex; flex: none; color: var(--text-dim); }
  .guide-body { padding: 0 var(--space-4) var(--space-4); font-size: var(--text-sm); line-height: 1.65; }
  .device-choice { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-3); margin-bottom: var(--space-3); }
  .device-choice > span { color: var(--text-dim); }
  select {
    min-height: 44px;
    max-width: 100%;
    padding: 8px 12px;
    border: 1px solid var(--line);
    border-radius: var(--radius);
    background: var(--surface-2);
    color: var(--text);
    font: inherit;
    font-size: 16px;
  }
  .install-button {
    min-height: 44px;
    padding: 10px 18px;
    border: 0;
    border-radius: var(--radius);
    background: var(--accent);
    color: var(--accent-ink);
    font: inherit;
    font-weight: var(--weight-semi);
    cursor: pointer;
    touch-action: manipulation;
  }
  .install-button:hover { background: var(--accent-hot); }
  .install-button:disabled { opacity: .7; cursor: wait; }
  summary:focus-visible, select:focus-visible, button:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 3px; }
  ol { margin: var(--space-3) 0; padding-left: 1.5rem; }
  li { padding-left: 3px; margin-block: var(--space-3); }
  li::marker { color: var(--accent); font-weight: var(--weight-semi); }
  strong { font-weight: var(--weight-semi); }
  .inline-icon { display: inline-flex; vertical-align: middle; color: var(--text); }
  .note { color: var(--text-dim); margin-top: var(--space-3); }
  .status { margin: var(--space-3) 0; color: var(--text-dim); }
  .status:empty { display: none; }
</style>
